import styles from './edit-instructor-dialog.module.scss';
import { Col, Row } from 'reactstrap';
import { useImmer } from 'use-immer';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { makeCourseRequest, useCourseGetRequestHook } from 'libs/webkua-lib/src/lib/requestUtils';
import { Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, LoadingButton, SnackBarContext, TextField } from '@frontend-workspace/react-shared-lib';
import { IInstructor } from '../../../../_models/instructor';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface EditInstructorDialogProps {
  onClose: () => void,
  open: boolean,
  refreshData: () => void,
  instructorId: string
}

export function EditInstructorDialog(props: EditInstructorDialogProps) {
  const { data }: { data: IInstructor } = useCourseGetRequestHook(`resource/instructor/${props.instructorId}`, undefined);
  const [instructor, updateInstructor] = useImmer(data);

  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [loading, setLoading] = useState<boolean>(false);

  const handleAdskInstructorIdChange = (event: ChangeEvent<HTMLInputElement>) => updateInstructor(draft => { draft.adskInstructorId = event.target.value });
  const handleFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => updateInstructor(draft => { draft.person.firstName = event.target.value });
  const handleLastNameChange = (event: ChangeEvent<HTMLInputElement>) => updateInstructor(draft => { draft.person.lastName = event.target.value });
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => updateInstructor(draft => { draft.person.email = event.target.value });
  const handleCompanyChange = (event: ChangeEvent<HTMLInputElement>) => updateInstructor(draft => { draft.person.companyName = event.target.value });
  const handleCityChange = (event: ChangeEvent<HTMLInputElement>) => updateInstructor(draft => { draft.city = event.target.value });

  useEffect(() => {
    if (data) {
      updateInstructor(data);
    }
  }, [data]);

  const missingFields = (): boolean => {
    const requiredFieldsFilled =
      instructor?.person?.firstName &&
      instructor?.person?.lastName &&
      instructor?.person?.email &&
      instructor?.person?.companyName &&
      instructor?.city;
    return requiredFieldsFilled ? false : true;
  };

  // Todo::
  // Status: Får ikke testet fordi epostene må finnes i SuperOffice, og i test er de slik: '@mailinator.com'
  const handleUpdateInstructor = () => {
    setLoading(true);
    makeCourseRequest(`resource/instructor/upsert`, 'POST', instructor).then(result => {
      setSnackbarMessage({ content: t('editInstructor.changedInstructorInList'), type: "info", });
      setLoading(false);
      props.onClose();
    }).catch(() => {
      // Uses app.tsx => error.response.data => SnackbarMessage
      // setSnackbarMessage({ content: "Kunne ikke endre instruktør i listen.", type: "error" })
    });
  };

  return (
    <Dialog
      open={props.open}
      maxWidth="sm"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t('editInstructor.changeInstructor')}
      </DialogTitle>
      <DialogContent>
        <Row>
          <Col xl={6}>
            <div className='mb-3'>
              <TextField
                label={t('standard.adskInstructorId')}
                name='adskInstructorId'
                fullWidth
                sx={{ minWidth: 300 }}
                value={instructor?.adskInstructorId || ''}
                onChange={handleAdskInstructorIdChange}
              />
            </div>
          </Col>
        </Row>
        <div className='mb-3'>
          <TextField
            label={<span>{t('standard.firstName')} &#42;</span>}
            name='firstName'
            fullWidth
            sx={{ minWidth: 300 }}
            value={instructor?.person?.firstName || ''}
            onChange={handleFirstNameChange}
          />
        </div>
        <div className='mb-3'>
          <TextField
            label={<span>{t('standard.lastName')} &#42;</span>}
            name='lastName'
            fullWidth
            sx={{ minWidth: 300 }}
            value={instructor?.person?.lastName || ''}
            onChange={handleLastNameChange}
          />
        </div>
        <div className='mb-3'>
          <TextField
            label={<span>{t('standard.email')} &#42;</span>}
            name='email'
            fullWidth
            sx={{ minWidth: 300 }}
            value={instructor?.person?.email || ''}
            onChange={handleEmailChange}
          />
        </div>
        <div className='mb-3'>
          <TextField
            label={<span>{t('standard.company')} &#42;</span>}
            name='company'
            fullWidth
            sx={{ minWidth: 300 }}
            value={instructor?.person?.companyName || ''}
            onChange={handleCompanyChange}
          />
        </div>
        <div className='mb-3'>
          <TextField
            label={<span>{t('standard.city')} &#42;</span>}
            name='city'
            fullWidth
            sx={{ minWidth: 300 }}
            value={instructor?.city || ''}
            onChange={handleCityChange}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          size='large'
          onClick={handleUpdateInstructor}
          disabled={loading || missingFields()}
          loading={loading}
          className='minButtonWidth'>
          {loading ? '' : t('editInstructor.updateInstructor')}
        </LoadingButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default EditInstructorDialog;
